/**
 * MHR API Functions
 *
 * All API calls for the Medical History Review module.
 */

import api from '../../services/api';
import type {
  Protocol,
  ProtocolFormData,
  ProtocolListResponse,
  ProtocolStats,
  Criterion,
  CriterionFormData,
  CriteriaTemplatesByCategory,
  CriterionCategory,
  MHRPatient,
  MHRDocument,
  DocumentCategory,
  CostEstimate,
  DocumentProcessingStatus,
  MedicalHistorySummary,
  DiagnosisEntry,
  MedicationEntry,
  ProcedureEntry,
  ConflictingEntry,
  GenerateSummaryResponse,
  UpdateEntryRequest,
  AddEntryRequest,
  Review,
  ReviewStatusResponse,
  VerificationPayload,
  ExportResponse,
  QuickMatchResult,
  MultiStudyEvaluation,
  PatientWithStats,
  HistoryLog,
  HistoryDiagnosis,
  HistoryMedication,
  HistoryProcedure,
  MergeSuggestion,
  MedDxLink,
  DiscrepancyRecord,
  HistorySnapshot,
  HistoryEdit,
} from '../types';

// =====================================================
// PROTOCOL API
// =====================================================

/**
 * List protocols for the current site
 */
export async function listProtocols(options?: {
  status?: string | string[];
  search?: string;
  limit?: number;
  offset?: number;
  orderBy?: string;
  orderDirection?: 'asc' | 'desc';
}): Promise<ProtocolListResponse> {
  const params = new URLSearchParams();

  if (options?.status) {
    if (Array.isArray(options.status)) {
      options.status.forEach(s => params.append('status', s));
    } else {
      params.set('status', options.status);
    }
  }
  if (options?.search) params.set('search', options.search);
  if (options?.limit) params.set('limit', options.limit.toString());
  if (options?.offset) params.set('offset', options.offset.toString());
  if (options?.orderBy) params.set('orderBy', options.orderBy);
  if (options?.orderDirection) params.set('orderDirection', options.orderDirection);

  const response = await api.get(`/api/mhr/protocols?${params.toString()}`);
  return response.data;
}

/**
 * Get a single protocol by ID
 */
export async function getProtocol(protocolId: string): Promise<Protocol> {
  const response = await api.get(`/api/mhr/protocols/${protocolId}`);
  return response.data;
}

/**
 * Get protocol statistics
 */
export async function getProtocolStats(protocolId: string): Promise<ProtocolStats> {
  const response = await api.get(`/api/mhr/protocols/${protocolId}/stats`);
  return response.data;
}

/**
 * Create a new protocol
 */
export async function createProtocol(data: ProtocolFormData): Promise<Protocol> {
  const response = await api.post('/api/mhr/protocols', data);
  return response.data;
}

/**
 * Update a protocol
 */
export async function updateProtocol(protocolId: string, data: Partial<ProtocolFormData>): Promise<Protocol> {
  const response = await api.patch(`/api/mhr/protocols/${protocolId}`, data);
  return response.data;
}

/**
 * Close (soft delete) a protocol
 */
export async function closeProtocol(protocolId: string): Promise<{ success: boolean; data: Protocol }> {
  const response = await api.delete(`/api/mhr/protocols/${protocolId}`);
  return response.data;
}

/**
 * Duplicate a protocol with its criteria
 */
export async function duplicateProtocol(
  protocolId: string,
  options?: {
    target_site_id?: string;
    overrides?: Partial<ProtocolFormData>;
  }
): Promise<Protocol> {
  const response = await api.post(`/api/mhr/protocols/${protocolId}/duplicate`, options || {});
  return response.data;
}

// =====================================================
// CRITERIA API
// =====================================================

/**
 * List criteria for a protocol
 */
export async function listCriteria(
  protocolId: string,
  options?: {
    type?: 'inclusion' | 'exclusion';
    category?: CriterionCategory;
    includeInactive?: boolean;
  }
): Promise<Criterion[]> {
  const params = new URLSearchParams();

  if (options?.type) params.set('type', options.type);
  if (options?.category) params.set('category', options.category);
  if (options?.includeInactive) params.set('includeInactive', 'true');

  const response = await api.get(`/api/mhr/protocols/${protocolId}/criteria?${params.toString()}`);
  return response.data;
}

/**
 * Create a new criterion
 */
export async function createCriterion(protocolId: string, data: CriterionFormData): Promise<Criterion> {
  const response = await api.post(`/api/mhr/protocols/${protocolId}/criteria`, data);
  return response.data;
}

/**
 * Update a criterion
 */
export async function updateCriterion(
  protocolId: string,
  criterionId: string,
  data: Partial<CriterionFormData>
): Promise<Criterion> {
  const response = await api.patch(`/api/mhr/protocols/${protocolId}/criteria/${criterionId}`, data);
  return response.data;
}

/**
 * Delete (soft) a criterion
 */
export async function deleteCriterion(
  protocolId: string,
  criterionId: string
): Promise<{ success: boolean; data: Criterion }> {
  const response = await api.delete(`/api/mhr/protocols/${protocolId}/criteria/${criterionId}`);
  return response.data;
}

/**
 * Reorder criteria
 */
export async function reorderCriteria(
  protocolId: string,
  ordering: Array<{ id: string; criterion_number: string }>
): Promise<{ success: boolean }> {
  const response = await api.post(`/api/mhr/protocols/${protocolId}/criteria/reorder`, { ordering });
  return response.data;
}

/**
 * Generate AI search terms for a criterion
 */
export async function generateSearchTerms(
  protocolId: string,
  data: {
    criterion_text: string;
    structured_definition?: object;
  }
): Promise<{ search_terms: string[] }> {
  const response = await api.post(`/api/mhr/protocols/${protocolId}/criteria/generate-search-terms`, data);
  return response.data;
}

/**
 * Batch generate search terms for multiple criteria
 */
export async function batchGenerateSearchTerms(
  protocolId: string,
  criteriaIds?: string[]
): Promise<{
  success: boolean;
  updated_count: number;
  results: Record<string, string[]>;
}> {
  const response = await api.post(`/api/mhr/protocols/${protocolId}/criteria/batch-generate-search-terms`, {
    criteria_ids: criteriaIds,
  });
  return response.data;
}

/**
 * Get criteria templates
 */
export async function getCriteriaTemplates(category?: CriterionCategory): Promise<CriteriaTemplatesByCategory> {
  const params = category ? `?category=${category}` : '';
  const response = await api.get(`/api/mhr/criteria-templates${params}`);
  return response.data;
}

/**
 * Get valid criteria categories
 */
export async function getCriteriaCategories(): Promise<CriterionCategory[]> {
  const response = await api.get('/api/mhr/criteria-categories');
  return response.data;
}

// =====================================================
// BUDGET API (from Phase 1)
// =====================================================

export interface BudgetConfig {
  per_review_limit: number;
  monthly_budget: number;
  monthly_warning_threshold: number;
}

export interface MonthlyUsage {
  reviews_completed: number;
  total_cost: number;
  total_pages_processed: number;
}

/**
 * Get budget configuration for the site
 */
export async function getBudgetConfig(): Promise<BudgetConfig> {
  const response = await api.get('/api/mhr/budget/config');
  return response.data;
}

/**
 * Get monthly usage for the current site
 */
export async function getMonthlyUsage(yearMonth?: string): Promise<MonthlyUsage> {
  const params = yearMonth ? `?year_month=${yearMonth}` : '';
  const response = await api.get(`/api/mhr/budget/usage${params}`);
  return response.data;
}

export interface BudgetConfigFull extends BudgetConfig {
  admin_per_review_limit: number;
  super_admin_unlimited: boolean;
}

export interface BudgetApproval {
  id: string;
  site_id: string;
  estimate_id: string;
  requested_by: string;
  status: 'pending' | 'approved' | 'denied';
  requested_at: string;
  reviewed_by?: string;
  reviewed_at?: string;
  review_notes?: string;
  estimate: {
    id: string;
    total_estimated_cost: number;
    page_count: number;
    criteria_count: number;
  };
  requester: {
    full_name: string;
    email: string;
  };
}

export interface HistoricalUsageMonth {
  id: string;
  site_id: string;
  year_month: string;
  reviews_completed: number;
  total_cost: number;
  total_pages_processed: number;
  budget_limit: number;
}

export interface CostRates {
  nativeExtraction: number;
  textractOcr: number;
  claudeVisionVerification: number;
  claudeInputTokens: number;
  claudeOutputTokens: number;
  claudeCachedInputTokens: number;
}

/**
 * Update budget configuration for the site
 */
export async function updateBudgetConfig(config: Partial<BudgetConfigFull>): Promise<BudgetConfigFull> {
  const response = await api.put('/api/mhr/budget/config', config);
  return response.data;
}

/**
 * Get pending budget approval requests
 */
export async function getApprovals(): Promise<BudgetApproval[]> {
  const response = await api.get('/api/mhr/budget/approvals');
  return response.data;
}

/**
 * Approve a budget request
 */
export async function approveRequest(id: string, notes?: string): Promise<BudgetApproval> {
  const response = await api.post(`/api/mhr/budget/approvals/${id}/approve`, { review_notes: notes });
  return response.data;
}

/**
 * Deny a budget request
 */
export async function denyRequest(id: string, notes?: string): Promise<BudgetApproval> {
  const response = await api.post(`/api/mhr/budget/approvals/${id}/deny`, { review_notes: notes });
  return response.data;
}

/**
 * Get historical usage data
 */
export async function getHistoricalUsage(months?: number): Promise<HistoricalUsageMonth[]> {
  const params = months ? `?months=${months}` : '';
  const response = await api.get(`/api/mhr/budget/historical-usage${params}`);
  return response.data;
}

/**
 * Get current cost rates
 */
export async function getCostRates(): Promise<CostRates> {
  const response = await api.get('/api/mhr/cost-rates');
  return response.data;
}

// =====================================================
// PATIENT API
// =====================================================

export interface PatientFormData {
  firstName: string;
  lastName: string;
  dob: string;
}

/**
 * List patients for the current site
 */
export async function listPatients(options?: {
  search?: string;
  limit?: number;
  offset?: number;
}): Promise<{ data: MHRPatient[]; total: number }> {
  const params = new URLSearchParams();

  if (options?.search) params.set('search', options.search);
  if (options?.limit) params.set('limit', options.limit.toString());
  if (options?.offset) params.set('offset', options.offset.toString());

  const response = await api.get(`/api/mhr/patients?${params.toString()}`);
  return response.data;
}

/**
 * Create a new patient
 */
export async function createPatient(data: PatientFormData): Promise<MHRPatient> {
  const response = await api.post('/api/mhr/patients', data);
  return response.data;
}

/**
 * Get a single patient by ID
 */
export async function getPatient(patientId: string): Promise<MHRPatient> {
  const response = await api.get(`/api/mhr/patients/${patientId}`);
  return response.data;
}

/**
 * List patients with history log stats (for Patient Database)
 */
export async function listPatientsWithStats(options?: {
  search?: string;
  limit?: number;
  offset?: number;
}): Promise<{ data: PatientWithStats[]; total: number }> {
  const params = new URLSearchParams();

  if (options?.search) params.set('search', options.search);
  if (options?.limit) params.set('limit', options.limit.toString());
  if (options?.offset) params.set('offset', options.offset.toString());
  params.set('include_stats', 'true');

  const response = await api.get(`/api/mhr/patients?${params.toString()}`);
  return response.data;
}

/**
 * Delete (soft delete) a patient profile
 */
export async function deletePatient(patientId: string): Promise<{ success: boolean }> {
  const response = await api.delete(`/api/mhr/patients/${patientId}`);
  return response.data;
}

/**
 * Get full patient chart data
 */
export async function getPatientChart(patientId: string): Promise<{
  patient: MHRPatient;
  history_log: HistoryLog | null;
  documents: MHRDocument[];
  recent_reviews: Review[];
}> {
  const response = await api.get(`/api/mhr/patients/${patientId}/chart`);
  return response.data;
}

// =====================================================
// HISTORY LOG API
// =====================================================

/**
 * Create a history log for a patient
 */
export async function createHistoryLog(patientId: string): Promise<HistoryLog> {
  const response = await api.post(`/api/mhr/patients/${patientId}/history-log`);
  return response.data;
}

/**
 * Get patient's history log with all entries
 */
export async function getHistoryLog(patientId: string): Promise<{
  log: HistoryLog;
  diagnoses: HistoryDiagnosis[];
  medications: HistoryMedication[];
  procedures: HistoryProcedure[];
}> {
  const response = await api.get(`/api/mhr/patients/${patientId}/history-log`);
  return response.data;
}

/**
 * Get history log by ID
 */
export async function getHistoryLogById(logId: string): Promise<HistoryLog> {
  const response = await api.get(`/api/mhr/history-logs/${logId}`);
  return response.data;
}

// History Log entry CRUD
export async function addHistoryDiagnosis(logId: string, data: Partial<HistoryDiagnosis>): Promise<HistoryDiagnosis> {
  const response = await api.post(`/api/mhr/history-logs/${logId}/diagnoses`, data);
  return response.data;
}

export async function updateHistoryDiagnosis(logId: string, entryId: string, data: Partial<HistoryDiagnosis>): Promise<HistoryDiagnosis> {
  const response = await api.patch(`/api/mhr/history-logs/${logId}/diagnoses/${entryId}`, data);
  return response.data;
}

export async function deleteHistoryDiagnosis(logId: string, entryId: string): Promise<{ success: boolean }> {
  const response = await api.delete(`/api/mhr/history-logs/${logId}/diagnoses/${entryId}`);
  return response.data;
}

export async function addHistoryMedication(logId: string, data: Partial<HistoryMedication>): Promise<HistoryMedication> {
  const response = await api.post(`/api/mhr/history-logs/${logId}/medications`, data);
  return response.data;
}

export async function updateHistoryMedication(logId: string, entryId: string, data: Partial<HistoryMedication>): Promise<HistoryMedication> {
  const response = await api.patch(`/api/mhr/history-logs/${logId}/medications/${entryId}`, data);
  return response.data;
}

export async function deleteHistoryMedication(logId: string, entryId: string): Promise<{ success: boolean }> {
  const response = await api.delete(`/api/mhr/history-logs/${logId}/medications/${entryId}`);
  return response.data;
}

export async function addHistoryProcedure(logId: string, data: Partial<HistoryProcedure>): Promise<HistoryProcedure> {
  const response = await api.post(`/api/mhr/history-logs/${logId}/procedures`, data);
  return response.data;
}

export async function updateHistoryProcedure(logId: string, entryId: string, data: Partial<HistoryProcedure>): Promise<HistoryProcedure> {
  const response = await api.patch(`/api/mhr/history-logs/${logId}/procedures/${entryId}`, data);
  return response.data;
}

export async function deleteHistoryProcedure(logId: string, entryId: string): Promise<{ success: boolean }> {
  const response = await api.delete(`/api/mhr/history-logs/${logId}/procedures/${entryId}`);
  return response.data;
}

/**
 * Verify a history log entry
 */
export async function verifyHistoryEntry(
  logId: string,
  entryType: 'diagnoses' | 'medications' | 'procedures',
  entryId: string
): Promise<{ verified_at: string; verified_by: string }> {
  const response = await api.post(`/api/mhr/history-logs/${logId}/${entryType}/${entryId}/verify`);
  return response.data;
}

/**
 * Get history log edit audit trail
 */
export async function getHistoryEditHistory(logId: string): Promise<HistoryEdit[]> {
  const response = await api.get(`/api/mhr/history-logs/${logId}/edit-history`);
  return response.data;
}

/**
 * Trigger aggregation for a specific document into patient history
 */
export async function aggregateDocument(logId: string, documentId: string): Promise<{
  success: boolean;
  promoted: { diagnoses: number; medications: number; procedures: number };
  merge_suggestions: number;
}> {
  const response = await api.post(`/api/mhr/history-logs/${logId}/aggregate/${documentId}`);
  return response.data;
}

// =====================================================
// MERGE SUGGESTIONS API
// =====================================================

export async function getMergeSuggestions(logId: string): Promise<MergeSuggestion[]> {
  const response = await api.get(`/api/mhr/history-logs/${logId}/merge-suggestions`);
  return response.data;
}

export async function acceptMergeSuggestion(logId: string, suggestionId: string): Promise<{ success: boolean }> {
  const response = await api.post(`/api/mhr/history-logs/${logId}/merge-suggestions/${suggestionId}/accept`);
  return response.data;
}

export async function rejectMergeSuggestion(logId: string, suggestionId: string): Promise<{ success: boolean }> {
  const response = await api.post(`/api/mhr/history-logs/${logId}/merge-suggestions/${suggestionId}/reject`);
  return response.data;
}

export async function batchAcceptMergeSuggestions(
  logId: string,
  suggestionIds?: string[]
): Promise<{ success: boolean; accepted_count: number; failed_count: number; failures: Array<{ id: string; error: string }> }> {
  const response = await api.post(`/api/mhr/history-logs/${logId}/merge-suggestions/batch-accept`, {
    suggestion_ids: suggestionIds,
  });
  return response.data;
}

export async function batchRejectMergeSuggestions(
  logId: string,
  suggestionIds?: string[]
): Promise<{ success: boolean; rejected_count: number }> {
  const response = await api.post(`/api/mhr/history-logs/${logId}/merge-suggestions/batch-reject`, {
    suggestion_ids: suggestionIds,
  });
  return response.data;
}

// =====================================================
// MED-DX LINKS API
// =====================================================

export async function getMedDxLinks(logId: string): Promise<MedDxLink[]> {
  const response = await api.get(`/api/mhr/history-logs/${logId}/med-dx-links`);
  return response.data;
}

export async function createMedDxLink(logId: string, data: {
  medication_id: string;
  diagnosis_id: string;
  link_type?: string;
  rationale?: string;
}): Promise<MedDxLink> {
  const response = await api.post(`/api/mhr/history-logs/${logId}/med-dx-links`, data);
  return response.data;
}

export async function deleteMedDxLink(logId: string, linkId: string): Promise<{ success: boolean }> {
  const response = await api.delete(`/api/mhr/history-logs/${logId}/med-dx-links/${linkId}`);
  return response.data;
}

export async function confirmMedDxLink(logId: string, linkId: string): Promise<MedDxLink> {
  const response = await api.patch(`/api/mhr/history-logs/${logId}/med-dx-links/${linkId}/confirm`);
  return response.data;
}

export async function suggestMedDxLinks(logId: string): Promise<{ suggestions: MedDxLink[]; cost: number }> {
  const response = await api.post(`/api/mhr/history-logs/${logId}/med-dx-links/suggest`);
  return response.data;
}

// =====================================================
// DISCREPANCY API
// =====================================================

export async function getDiscrepancies(logId: string): Promise<DiscrepancyRecord[]> {
  const response = await api.get(`/api/mhr/history-logs/${logId}/discrepancies`);
  return response.data;
}

export async function createDiscrepancy(logId: string, data: Partial<DiscrepancyRecord>): Promise<DiscrepancyRecord> {
  const response = await api.post(`/api/mhr/history-logs/${logId}/discrepancies`, data);
  return response.data;
}

export async function updateDiscrepancy(logId: string, discrepancyId: string, data: Partial<DiscrepancyRecord>): Promise<DiscrepancyRecord> {
  const response = await api.patch(`/api/mhr/history-logs/${logId}/discrepancies/${discrepancyId}`, data);
  return response.data;
}

// =====================================================
// SNAPSHOT API
// =====================================================

export async function getSnapshots(logId: string): Promise<HistorySnapshot[]> {
  const response = await api.get(`/api/mhr/history-logs/${logId}/snapshots`);
  return response.data;
}

export async function createSnapshot(logId: string, data: {
  snapshot_name: string;
  snapshot_type?: string;
  description?: string;
}): Promise<HistorySnapshot> {
  const response = await api.post(`/api/mhr/history-logs/${logId}/snapshots`, data);
  return response.data;
}

export async function getSnapshot(logId: string, snapshotId: string): Promise<HistorySnapshot> {
  const response = await api.get(`/api/mhr/history-logs/${logId}/snapshots/${snapshotId}`);
  return response.data;
}

// =====================================================
// REVIEW FROM HISTORY LOG API
// =====================================================

/**
 * Start a protocol review from a patient's history log
 */
export async function startReviewFromHistoryLog(logId: string, protocolId: string): Promise<{
  success: boolean;
  reviewId?: string;
  requiresApproval?: boolean;
  message?: string;
}> {
  const response = await api.post(`/api/mhr/history-logs/${logId}/start-review`, {
    protocol_id: protocolId,
  });
  return response.data;
}

// =====================================================
// DOCUMENT API
// =====================================================

/**
 * Upload a document for a patient
 */
export async function uploadDocument(
  patientId: string,
  file: File,
  onProgress?: (progress: number) => void
): Promise<MHRDocument> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('patient_id', patientId);

  const response = await api.post('/api/mhr/documents/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    onUploadProgress: event => {
      if (event.total && onProgress) {
        const progress = Math.round((event.loaded / event.total) * 100);
        onProgress(progress);
      }
    },
  });

  return response.data;
}

/**
 * Batch upload documents for a patient
 */
export async function batchUploadDocuments(
  patientId: string,
  files: File[],
  onProgress?: (fileId: string, progress: number) => void
): Promise<MHRDocument[]> {
  const formData = new FormData();
  formData.append('patient_id', patientId);
  files.forEach(file => {
    formData.append('files', file);
  });

  const response = await api.post('/api/mhr/documents/batch-upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  return response.data;
}

/**
 * Get document processing status
 */
export async function getDocumentStatus(documentId: string): Promise<DocumentProcessingStatus> {
  const response = await api.get(`/api/mhr/documents/${documentId}/status`);
  return response.data;
}

/**
 * Get presigned S3 URL for downloading a document
 */
export async function getDocumentDownloadUrl(documentId: string): Promise<string> {
  const response = await api.get(`/api/mhr/documents/${documentId}/download`);
  return response.data.url;
}

/**
 * Fetch document PDF as a blob URL for in-app viewing (avoids S3 CORS issues)
 */
export async function getDocumentViewUrl(documentId: string): Promise<string> {
  const response = await api.get(`/api/mhr/documents/${documentId}/view`, {
    responseType: 'blob',
  });
  return URL.createObjectURL(response.data);
}

/**
 * List documents for a patient with optional filtering and sorting
 */
export async function listDocuments(
  patientId: string,
  options?: {
    status?: MHRDocument['extraction_status'];
    category?: DocumentCategory;
    sort?: 'uploaded_at' | 'document_date' | 'document_category' | 'filename';
    order?: 'asc' | 'desc';
  }
): Promise<MHRDocument[]> {
  const params = new URLSearchParams();
  if (options?.status) params.set('status', options.status);
  if (options?.category) params.set('category', options.category);
  if (options?.sort) params.set('sort', options.sort);
  if (options?.order) params.set('order', options.order);

  const response = await api.get(`/api/mhr/patients/${patientId}/documents?${params.toString()}`);
  return response.data;
}

/**
 * Search documents for a patient using full-text search
 */
export async function searchDocuments(
  patientId: string,
  options: {
    q: string;
    category?: DocumentCategory;
    dateFrom?: string;
    dateTo?: string;
  }
): Promise<{ documents: MHRDocument[]; total: number }> {
  const params = new URLSearchParams();
  params.set('q', options.q);
  if (options.category) params.set('category', options.category);
  if (options.dateFrom) params.set('dateFrom', options.dateFrom);
  if (options.dateTo) params.set('dateTo', options.dateTo);

  const response = await api.get(`/api/mhr/patients/${patientId}/documents/search?${params.toString()}`);
  return response.data;
}

/**
 * Update document metadata (category, date, facility, description, tags)
 */
export async function updateDocumentMetadata(
  documentId: string,
  data: {
    document_category?: DocumentCategory;
    document_date?: string | null;
    date_range_start?: string | null;
    date_range_end?: string | null;
    facility_name?: string | null;
    description?: string | null;
    tags?: string[];
  }
): Promise<MHRDocument> {
  const response = await api.patch(`/api/mhr/documents/${documentId}/metadata`, data);
  return response.data;
}

/**
 * Get cost estimate for processing documents
 */
export async function getCostEstimate(
  documentId: string,
  protocolId?: string
): Promise<CostEstimate> {
  const params = new URLSearchParams();
  if (protocolId) params.set('protocol_id', protocolId);

  const response = await api.post(`/api/mhr/documents/${documentId}/estimate`, { protocol_id: protocolId });
  return response.data;
}

/**
 * Start document processing (synchronous)
 */
export async function startProcessing(documentId: string): Promise<{ success: boolean; document: MHRDocument }> {
  const response = await api.post(`/api/mhr/documents/${documentId}/process`);
  return response.data;
}

// =====================================================
// ASYNC PROCESSING API (Background Jobs)
// =====================================================

export interface ProcessingJob {
  id: string;
  documentId: string;
  state: 'pending' | 'layer1_extraction' | 'layer2_ocr' | 'layer3_vision' | 'completed' | 'failed';
  createdAt: string;
  startedAt: string | null;
  completedAt: string | null;
  progress: {
    overall: number;
    currentLayer: number;
    pagesProcessed: number;
    totalPages: number;
    layerProgress: {
      layer1: { status: 'pending' | 'processing' | 'complete'; progress: number; pagesProcessed: number };
      layer2: { status: 'pending' | 'processing' | 'complete'; progress: number; pagesProcessed: number };
      layer3: { status: 'pending' | 'processing' | 'complete'; progress: number; pagesProcessed: number };
    };
  };
  costs: {
    extraction: number;
    ocr: number;
    vision: number;
    total: number;
  };
  checkpoint: {
    layer: number;
    page: number;
    lastUpdate: string | null;
  };
  error: string | null;
  result: any;
}

export interface QueueProcessingResponse {
  success: boolean;
  jobId: string;
  documentId: string;
  status: string;
  message: string;
  websocketUrl: string;
}

/**
 * Start async document processing (background job with WebSocket updates)
 */
export async function startAsyncProcessing(
  documentId: string,
  options?: {
    skipOcr?: boolean;
    skipVision?: boolean;
  }
): Promise<QueueProcessingResponse> {
  const response = await api.post(`/api/mhr/documents/${documentId}/process-async`, options || {});
  return response.data;
}

/**
 * Get status of a processing job
 */
export async function getJobStatus(jobId: string): Promise<ProcessingJob> {
  const response = await api.get(`/api/mhr/jobs/${jobId}`);
  return response.data;
}

/**
 * Get all jobs for a document
 */
export async function getDocumentJobs(documentId: string): Promise<{ jobs: ProcessingJob[] }> {
  const response = await api.get(`/api/mhr/documents/${documentId}/jobs`);
  return response.data;
}

/**
 * Cancel a processing job
 */
export async function cancelJob(jobId: string): Promise<{ success: boolean; message: string }> {
  const response = await api.post(`/api/mhr/jobs/${jobId}/cancel`);
  return response.data;
}

/**
 * Resume a failed job
 */
export async function resumeJob(jobId: string): Promise<{ jobId: string; status: string; originalJobId: string }> {
  const response = await api.post(`/api/mhr/jobs/${jobId}/resume`);
  return response.data;
}

/**
 * Get job queue statistics (admin)
 */
export async function getQueueStats(): Promise<{
  total: number;
  pending: number;
  processing: number;
  completed: number;
  failed: number;
}> {
  const response = await api.get('/api/mhr/jobs/queue/stats');
  return response.data;
}

// =====================================================
// SUMMARY API
// =====================================================

/**
 * Generate a medical history summary for a document
 */
export async function generateSummary(documentId: string): Promise<GenerateSummaryResponse> {
  const response = await api.post(`/api/mhr/documents/${documentId}/summary/generate`);
  return response.data;
}

/**
 * Get existing summary for a document
 */
export async function getSummary(documentId: string): Promise<{
  summary: MedicalHistorySummary;
  diagnoses: DiagnosisEntry[];
  medications: MedicationEntry[];
  procedures: ProcedureEntry[];
  conflicts: ConflictingEntry[];
}> {
  const response = await api.get(`/api/mhr/documents/${documentId}/summary`);
  return response.data;
}

/**
 * Update a diagnosis entry
 */
export async function updateDiagnosis(
  summaryId: string,
  diagnosisId: string,
  data: UpdateEntryRequest
): Promise<DiagnosisEntry> {
  const response = await api.patch(`/api/mhr/summaries/${summaryId}/diagnoses/${diagnosisId}`, data);
  return response.data;
}

/**
 * Update a medication entry
 */
export async function updateMedication(
  summaryId: string,
  medicationId: string,
  data: UpdateEntryRequest
): Promise<MedicationEntry> {
  const response = await api.patch(`/api/mhr/summaries/${summaryId}/medications/${medicationId}`, data);
  return response.data;
}

/**
 * Update a procedure entry
 */
export async function updateProcedure(
  summaryId: string,
  procedureId: string,
  data: UpdateEntryRequest
): Promise<ProcedureEntry> {
  const response = await api.patch(`/api/mhr/summaries/${summaryId}/procedures/${procedureId}`, data);
  return response.data;
}

/**
 * Add a new diagnosis entry
 */
export async function addDiagnosis(
  summaryId: string,
  data: AddEntryRequest
): Promise<DiagnosisEntry> {
  const response = await api.post(`/api/mhr/summaries/${summaryId}/diagnoses`, data);
  return response.data;
}

/**
 * Add a new medication entry
 */
export async function addMedication(
  summaryId: string,
  data: AddEntryRequest
): Promise<MedicationEntry> {
  const response = await api.post(`/api/mhr/summaries/${summaryId}/medications`, data);
  return response.data;
}

/**
 * Add a new procedure entry
 */
export async function addProcedure(
  summaryId: string,
  data: AddEntryRequest
): Promise<ProcedureEntry> {
  const response = await api.post(`/api/mhr/summaries/${summaryId}/procedures`, data);
  return response.data;
}

/**
 * Delete a diagnosis entry
 */
export async function deleteDiagnosis(
  summaryId: string,
  diagnosisId: string
): Promise<{ success: boolean }> {
  const response = await api.delete(`/api/mhr/summaries/${summaryId}/diagnoses/${diagnosisId}`);
  return response.data;
}

/**
 * Delete a medication entry
 */
export async function deleteMedication(
  summaryId: string,
  medicationId: string
): Promise<{ success: boolean }> {
  const response = await api.delete(`/api/mhr/summaries/${summaryId}/medications/${medicationId}`);
  return response.data;
}

/**
 * Delete a procedure entry
 */
export async function deleteProcedure(
  summaryId: string,
  procedureId: string
): Promise<{ success: boolean }> {
  const response = await api.delete(`/api/mhr/summaries/${summaryId}/procedures/${procedureId}`);
  return response.data;
}

/**
 * Verify a summary entry (diagnosis, medication, or procedure)
 */
export async function verifyEntry(
  summaryId: string,
  entryType: 'diagnoses' | 'medications' | 'procedures',
  entryId: string
): Promise<{ verified_at: string; verified_by: string }> {
  const response = await api.post(`/api/mhr/summaries/${summaryId}/${entryType}/${entryId}/verify`);
  return response.data;
}

/**
 * Approve the entire summary
 */
export async function approveSummary(summaryId: string): Promise<MedicalHistorySummary> {
  const response = await api.post(`/api/mhr/summaries/${summaryId}/approve`);
  return response.data;
}

/**
 * Export summary in specified format
 */
export async function exportSummary(
  summaryId: string,
  format: 'pdf' | 'json'
): Promise<{ data: any; contentType: string }> {
  const response = await api.get(`/api/mhr/summaries/${summaryId}/export`, {
    params: { format },
    responseType: format === 'pdf' ? 'blob' : 'json',
  });
  return {
    data: response.data,
    contentType: response.headers['content-type'] || (format === 'pdf' ? 'application/pdf' : 'application/json'),
  };
}

/**
 * Resolve a conflict
 */
export async function resolveConflict(
  summaryId: string,
  conflictId: string,
  resolutionNotes: string
): Promise<ConflictingEntry> {
  const response = await api.post(`/api/mhr/summaries/${summaryId}/conflicts/${conflictId}/resolve`, {
    resolution_notes: resolutionNotes,
  });
  return response.data;
}

/**
 * Get summary edit history
 */
export async function getSummaryEditHistory(
  summaryId: string
): Promise<Array<{
  id: string;
  entry_type: string;
  entry_id: string;
  edit_type: string;
  previous_value: Record<string, unknown>;
  new_value: Record<string, unknown>;
  edited_by: string;
  edited_at: string;
  reason?: string;
}>> {
  const response = await api.get(`/api/mhr/summaries/${summaryId}/history`);
  return response.data;
}

// =====================================================
// REVIEW API
// =====================================================

/**
 * Get a review by ID with full results
 */
export async function getReview(reviewId: string): Promise<Review> {
  const response = await api.get(`/api/mhr/reviews/${reviewId}`);
  return response.data;
}

/**
 * Get review status (for polling during analysis)
 */
export async function getReviewStatus(reviewId: string): Promise<ReviewStatusResponse> {
  const response = await api.get(`/api/mhr/reviews/${reviewId}/status`);
  return response.data;
}

/**
 * Get review cost breakdown
 */
export async function getReviewCost(reviewId: string): Promise<{
  total_cost: number;
  cache_savings: number;
  breakdown: {
    analysis_cost: number;
    input_tokens: number;
    output_tokens: number;
    cached_tokens: number;
  };
}> {
  const response = await api.get(`/api/mhr/reviews/${reviewId}/cost`);
  return response.data;
}

/**
 * Verify a criterion result with human review
 */
export async function verifyCriterion(
  reviewId: string,
  resultId: string,
  payload: VerificationPayload
): Promise<{ success: boolean; result_id: string }> {
  const response = await api.post(`/api/mhr/reviews/${reviewId}/verify/${resultId}`, payload);
  return response.data;
}

/**
 * Complete a review after all verifications
 */
export async function completeReview(reviewId: string): Promise<Review> {
  const response = await api.post(`/api/mhr/reviews/${reviewId}/complete`);
  return response.data;
}

/**
 * Export review results as PDF
 */
export async function exportReview(reviewId: string): Promise<ExportResponse> {
  const response = await api.post(`/api/mhr/reviews/${reviewId}/export`);
  return response.data;
}

/**
 * Get a document page image as a blob object URL.
 *
 * Fetches through the shared axios instance so the request carries auth/site
 * headers and honours baseURL (a bare <img src> string would 401 and ignore
 * baseURL). Mirrors getDocumentViewUrl. Caller owns the returned object URL and
 * must URL.revokeObjectURL() it when done.
 */
export async function getPageImage(documentId: string, pageNum: number, width?: number): Promise<string> {
  const params = width ? { width } : undefined;
  const response = await api.get(`/api/mhr/documents/${documentId}/page/${pageNum}/image`, {
    params,
    responseType: 'blob',
  });
  return URL.createObjectURL(response.data);
}

/**
 * List reviews for a patient or protocol
 */
export async function listReviews(params: {
  patient_id?: string;
  protocol_id?: string;
  status?: string;
}): Promise<Review[]> {
  const response = await api.get('/api/mhr/reviews', { params });
  return response.data;
}

/**
 * Start a new eligibility review
 */
export async function startReview(data: {
  patient_id: string;
  protocol_id: string;
  document_id: string;
}): Promise<{ review_id: string; status: string }> {
  const response = await api.post('/api/mhr/reviews', data);
  return response.data;
}

/**
 * Cancel an in-progress review
 */
export async function cancelReview(reviewId: string): Promise<{ success: boolean }> {
  const response = await api.post(`/api/mhr/reviews/${reviewId}/cancel`);
  return response.data;
}

// =====================================================
// MULTI-STUDY EVALUATION API
// =====================================================

/**
 * Quick match against protocols
 * Returns estimated match levels for each protocol based on existing review data
 */
export async function quickMatchProtocols(
  patientId: string,
  sourceReviewId: string,
  targetProtocolIds?: string[]
): Promise<QuickMatchResult[]> {
  const response = await api.post('/api/mhr/multi-study/quick-match', {
    patient_id: patientId,
    source_review_id: sourceReviewId,
    target_protocol_ids: targetProtocolIds
  });
  return response.data;
}

/**
 * Start multi-study evaluation
 * Evaluates a patient against multiple protocols, reusing criteria results where possible
 */
export async function startMultiStudyEvaluation(
  patientId: string,
  sourceReviewId: string,
  targetProtocolIds: string[]
): Promise<{ evaluation_id: string; status: string; estimated_cost: number }> {
  const response = await api.post('/api/mhr/multi-study/evaluate', {
    patient_id: patientId,
    source_review_id: sourceReviewId,
    target_protocol_ids: targetProtocolIds
  });
  return response.data;
}

/**
 * Get evaluation status/results
 * Returns the current state of a multi-study evaluation
 */
export async function getMultiStudyEvaluation(
  evaluationId: string
): Promise<MultiStudyEvaluation> {
  const response = await api.get(`/api/mhr/multi-study/${evaluationId}`);
  return response.data;
}
